import React, { createContext, useContext, useState, useCallback, ReactNode, useId, useMemo } from 'react';
import '../styles/error-handling.css';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  scope?: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  actions?: Array<{
    label: string;
    onClick: () => void;
  }>;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  removeToast: (id: string) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const scope = useId();
  const context = useContext(ToastContext);
  const scoped = useMemo(() => {
    if (!context) return undefined;
    const showToast = (toast: Omit<Toast, 'id'>) => context.showToast({...toast, scope});
    return {...context, showToast,
      showSuccess: (title: string, message?: string) => showToast({type:'success', title, message}),
      showError: (title: string, message?: string) => showToast({type:'error', title, message, duration:7000}),
      showInfo: (title: string, message?: string) => showToast({type:'info', title, message}),
      showWarning: (title: string, message?: string) => showToast({type:'warning', title, message}),
    };
  // Methods stay stable when only toast state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.showToast, scope]);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return {...scoped!, toasts: context.toasts};
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast: Toast = { id, ...toast };

    setToasts(prev => [...prev.filter(item => !(toast.type === 'success' && item.type === 'error' && item.scope === toast.scope) && !(item.type === toast.type && item.title === toast.title && item.message === toast.message)), newToast].slice(-3));

    // Auto-remove after duration
    const minimum = toast.actions?.length ? 8000 : toast.type === 'error' ? 6000 : 4000;
    const duration = toast.duration === 0 ? 0 : Math.max(toast.duration ?? minimum, minimum);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const showSuccess = useCallback((title: string, message?: string) => {
    showToast({ type: 'success', title, message });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string) => {
    showToast({ type: 'error', title, message, duration: 7000 });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string) => {
    showToast({ type: 'info', title, message });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string) => {
    showToast({ type: 'warning', title, message });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      removeToast,
      toasts
    }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <AlertCircle size={20} />;
      case 'warning': return <AlertCircle size={20} />;
      case 'info': return <Info size={20} />;
      default: return <Info size={20} />;
    }
  };

  return (
    <div className={`toast toast-${toast.type}`}>
      <div className="toast-icon">
        {getIcon()}
      </div>
      <div className="toast-content">
        <div className="toast-title">{toast.title}</div>
        {toast.message && <div className="toast-message">{toast.message}</div>}
        {toast.actions && (
          <div className="toast-actions">
            {toast.actions.map((action, index) => (
              <button
                key={index}
                className="toast-action-btn"
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        className="toast-close"
        onClick={() => onRemove(toast.id)}
      >
        <X size={16} />
      </button>
    </div>
  );
};
